import React from 'react';
import { Box, Flex, Image, Link, Text, Grid } from '@chakra-ui/react';
import dayjs from 'dayjs';
import I18n from './I18n';
import Logo from './logo';
import iconTwitter from '../../static/new_images/footer/icon-twitter.png';
import iconDiscord from '../../static/new_images/footer/icon-discord.png';
import iconGitHub from '../../static/new_images/footer/icon-github.png';
import iconMeium from '../../static/new_images/footer/icon-medium.png';
import CustomLink from './customLink';
import gtagEvent from '../utils/gtag';

const SupportPages = [
  {
    title: 'Help Center',
    href: 'https://portto.zendesk.com/',
    titleTranslationKey: 'blocto.footer.helpCenter',
    isExternal: true,
  },
  {
    title: 'Feature Feedback',
    href: 'https://blocto.canny.io/feature-request',
    titleTranslationKey: 'blocto.footer.feedback',
    isExternal: true,
  },
  {
    title: 'Terms of Use',
    href: '/terms',
    titleTranslationKey: 'blocto.footer.termsOfUse',
  },
  {
    title: 'Privacy Policy',
    href: '/privacy',
    titleTranslationKey: 'blocto.footer.privacyPolicy',
  },
];

const SocialMedias = [
  {
    href: 'https://twitter.com/BloctoApp',
    name: 'Twitter',
    icon: iconTwitter,
  },
  {
    href: 'http://discord.gg/QRZTr6yHmY',
    name: 'Discord',
    icon: iconDiscord,
  },
  {
    href: 'https://github.com/blocto',
    name: 'GitHub',
    icon: iconGitHub,
  },
  {
    href: 'https://medium.portto.com',
    name: 'Medium',
    icon: iconMeium,
  },
];

const BloctoPages = [
  {
    title: 'About Us',
    href: `/about`,
    titleTranslationKey: 'blocto.footer.about.aboutus',
  },
  {
    title: 'Careers',
    href: `https://www.cakeresume.com/companies/portto`,
    titleTranslationKey: 'blocto.footer.about.careers',
    isExternal: true,
  },
];

const ResourcesPages = [
  {
    title: 'Developer',
    href: 'https://docs.blocto.app/',
    titleTranslationKey: 'blocto.footer.developer',
    isExternal: true,
  },
  {
    title: 'Join Community',
    href: 'http://discord.gg/QRZTr6yHmY',
    titleTranslationKey: 'blocto.footer.community',
    isExternal: true,
  },
  {
    title: 'Press Kit',
    href: 'https://portto.notion.site/Blocto-Brand-Kit-f41cbc8b42ec406b88d69a98237c040d',
    titleTranslationKey: 'blocto.footer.pressKit',
    isExternal: true,
  },
  {
    title: 'White Paper',
    href: 'https://drive.google.com/file/d/1C4TKAEzCcJ6uyiA8QBTCx78xE9szdnFU/view',
    titleTranslationKey: 'blocto.footer.whitePaper',
  },
];

const links = [
  { titleTranslationKey: 'blocto.footer.about', pages: BloctoPages },
  // { titleTranslationKey: 'blocto.footer.product', pages: ProductPages },
  { titleTranslationKey: 'blocto.footer.resources', pages: ResourcesPages },
  { titleTranslationKey: 'blocto.footer.support', pages: SupportPages },
];

const Footer = () => (
  <Box
    as="footer"
    bg="#F7F7F7"
    overflow="hidden"
    pb={{ base: '4px' }}
    borderRadius={{ base: '30px 30px 0 0', md: '60px 60px 0 0' }}
  >
    <Flex
      w="100%"
      wrap="wrap"
      justify={{ md: 'space-between' }}
      maxWidth={{ md: '1440px' }}
      m={{ md: '0 auto' }}
      p={{ base: '80px 24px 0 24px', md: '64px 64px 0 64px', lg: '120px 120px 0' }}
    >
      <Flex wrap="wrap" w={{ base: '100%', md: '30%' }} h={{ md: '128px' }}>
        <Flex w="100%">
          <CustomLink to="/" title="blocto">
            <Logo width={{ base: '165px' }} height={{ base: '56px' }} color="blue" />
          </CustomLink>
        </Flex>
        <Flex
          alignItems="center"
          maxWidth={{ base: '100%', md: '260px' }}
          marginTop={{ base: '32px' }}
          width="100%"
          marginBottom="0"
        >
          <Grid
            gap="16px"
            gridAutoFlow="column"
            width="100%"
            maxWidth="276px"
            gridAutoColumns={{ base: 'max-content' }}
          >
            {SocialMedias.map(({ href, name, icon }) => (
              <Link
                key={name}
                href={href}
                isExternal
                w="40px"
                h="40px"
                onClick={() => {
                  gtagEvent('bloctoweb_click_socialcommunity', {
                    location: 'footer',
                    social_community: name.toLowerCase(),
                    link_url: href,
                    link_domain: new URL(href).host,
                  });
                }}
              >
                <Image alt={name} src={icon} width="36px" height="36px" />
              </Link>
            ))}
          </Grid>
        </Flex>
      </Flex>

      <Flex
        display={{ base: 'flex', md: 'block', lg: 'flex' }}
        flexDirection={{ base: 'column', md: 'row' }}
        height={{ base: 'auto', lg: '362px' }}
        paddingBottom={{ base: '88px', md: '0' }}
        alignItems={{ base: 'stretch', md: 'flex-start' }}
        justifyContent="space-between"
        w={{ sm: '100%', md: '100%', lg: '49%' }}
        mt={{ md: '80px', lg: '0' }}
      >
        <Flex
          flex="1"
          flexWrap={{ base: 'wrap', md: 'unset' }}
          alignItems="flex-start"
          justifyContent="space-between"
        >
          {links.map(({ titleTranslationKey, pages }, idx) => (
            <Box
              key={titleTranslationKey}
              width={{ base: '100%', md: '33%' }}
              mt={{ base: idx === 0 ? '88px' : '44px', sm: '44px', md: '0' }}
            >
              <Text
                color="icon.text"
                fontSize="20px"
                fontWeight={500}
                lineHeight={{ base: '28px', md: '140%' }}
              >
                <I18n id={titleTranslationKey} />
              </Text>
              {pages.map((item, index) => (
                <CustomLink
                  key={item.title}
                  to={item.href}
                  display="block"
                  margin="12px 0"
                  color="icon.text"
                  fontSize={{ base: '14px', md: '16px' }}
                  lineHeight={{ base: '20px', md: '130%' }}
                  marginTop={index === 0 ? '28px' : undefined}
                  marginBottom={index === pages.length - 1 ? '0' : undefined}
                  isExternal={item.isExternal}
                  gtag={{
                    event: 'bloctoweb_click_footer',
                    properties:
                      item.href && item.href.startsWith('http')
                        ? {
                            link_url: item.href,
                            link_domain: new URL(item.href).host,
                          }
                        : undefined,
                  }}
                >
                  {item.titleTranslationKey ? <I18n id={item.titleTranslationKey} /> : item.title}
                </CustomLink>
              ))}
            </Box>
          ))}
        </Flex>
      </Flex>
    </Flex>

    <Box maxWidth="1440px" mx="auto">
      <Text
        fontSize="14px"
        lineHeight="20px"
        py={{ base: '20px', md: '24px' }}
        mx={{ base: '0', md: '64px', lg: '120px' }}
        color="icon.text"
        borderTop="solid 0.5px"
        borderColor="border.primary"
        textAlign={{ base: 'center', md: 'center' }}
        mt={{ md: '40px', lg: '0' }}
      >
        © {dayjs().year()} portto. All rights reserved.
      </Text>
    </Box>
  </Box>
);

export default Footer;
